/**
 * Constrains the position of an inner rectangle so it always stays inside of the bounding box.
 *
 * @param {object} boundingBox - The bounding box.
 * @param {object} innerRect - The inner rectangle.
 * @param {number} newX - Proposed X coordinate for the inner rectangle’s center.
 * @param {number} newY - Proposed Y coordinate for the inner rectangle’s center.
 *
 * @returns {fabric.Point} A corrected point (X, Y) that keeps the inner rectangle fully inside the bounding box.
 */
export const constrainPositionToBoundingBox = function (boundingBox, innerRect, newX, newY) {
    // Get the center and half-dimensions of the bounding box.
    const center = new fabric.Point(boundingBox.left, boundingBox.top);
    const halfWidth = boundingBox.width / 2;
    const halfHeight = boundingBox.height / 2;

    // Create a point from the given (x, y).
    let point = new fabric.Point(newX, newY);

    // "Unrotate" the point so the bounding box is axis-aligned.
    point = fabric.util.rotatePoint(point, center, fabric.util.degreesToRadians(-boundingBox.angle));

    // Find the relative angle between the inner rectangle and the bounding box.
    const relativeAngle = fabric.util.degreesToRadians(innerRect.angle - boundingBox.angle);

    // Compute the effective half-width and half-height of the inner rectangle.
    const effectiveHalfWidth =
        (Math.abs(innerRect.width * Math.cos(relativeAngle)) +
            Math.abs(innerRect.height * Math.sin(relativeAngle))) /
        2;
    const effectiveHalfHeight =
        (Math.abs(innerRect.width * Math.sin(relativeAngle)) +
            Math.abs(innerRect.height * Math.cos(relativeAngle))) /
        2;

    // Determine the allowed range for the inner rectangle's center.
    const minX = center.x - halfWidth + effectiveHalfWidth;
    const maxX = center.x + halfWidth - effectiveHalfWidth;
    const minY = center.y - halfHeight + effectiveHalfHeight;
    const maxY = center.y + halfHeight - effectiveHalfHeight;

    // Clamp the point within these allowed ranges.
    point.x = Math.min(Math.max(point.x, minX), maxX);
    point.y = Math.min(Math.max(point.y, minY), maxY);

    // Rotate the point back to the original coordinate system.
    return fabric.util.rotatePoint(point, center, fabric.util.degreesToRadians(boundingBox.angle));
};

// Size of a width x height rect rotated to `angle`, measured along the bounding box's own axes.
const getRotatedExtents = function (boundingBox, width, height, angle) {
    const relativeAngle = fabric.util.degreesToRadians(angle - boundingBox.angle);
    const absCos = Math.abs(Math.cos(relativeAngle));
    const absSin = Math.abs(Math.sin(relativeAngle));
    const extentWidth = width * absCos + height * absSin;
    const extentHeight = width * absSin + height * absCos;
    if (!isFinite(extentWidth) || !isFinite(extentHeight)) return null;
    return { width: extentWidth, height: extentHeight };
};

/**
 * Checks whether a rect of the given size and rotation fits inside the bounding box at SOME position,
 *  regardless of where it currently sits. Position is fixed afterwards with constrainPositionToBoundingBox.
 *
 * @param {object} boundingBox - The bounding box.
 * @param {number} width - Width of the inner rect.
 * @param {number} height - Height of the inner rect.
 * @param {number} angle - Absolute angle of the inner rect, in degrees.
 *
 * @returns {boolean} True if it fits (or the geometry is degenerate).
 */
export const fitsInsideBoundingBox = function (boundingBox, width, height, angle) {
    const extents = getRotatedExtents(boundingBox, width, height, angle);
    if (!extents) return true;
    return extents.width <= boundingBox.width && extents.height <= boundingBox.height;
};

/**
 * Calculates how much an inner rectangle can be scaled so it always fits inside of the bounding box
 *
 * @param {object} boundingBox - The bounding box.
 * @param {object} innerRect - The inner rectangle.
 * @param {number|null} [innerRectWidth=null] - Optional width to use instead of `innerRect.width`.
 * @param {number|null} [innerRectHeight=null] - Optional height to use instead of `innerRect.height`.
 *
 * @returns {object} An object with:
 *   - `scaleX`: Maximum horizontal scale factor allowed.
 *   - `scaleY`: Maximum vertical scale factor allowed.
 *   - `maxScale`: The limiting scale factor (minimum of `scaleX` and `scaleY`).
 *   - `width`: Maximum width that fits inside the bounding box.
 *   - `height`: Maximum height that fits inside the bounding box.
 */
export const constrainDimensionToBoundingBox = function (boundingBox, innerRect, innerRectWidth = null, innerRectHeight = null) {
    const bbAngleRad = fabric.util.degreesToRadians(boundingBox.angle);

    // Get the inner rectangle's center position in the coordinate system of the bounding box.
    // This is done by shifting its center relative to the bounding box and then rotating accordingly.
    const dx = innerRect.left - boundingBox.left;
    const dy = innerRect.top - boundingBox.top;
    const cosBB = Math.cos(-bbAngleRad);
    const sinBB = Math.sin(-bbAngleRad);
    const localX = dx * cosBB - dy * sinBB;
    const localY = dx * sinBB + dy * cosBB;

    // Determine how much space is available from the inner rectangle's center to the edges of the bounding box.
    const halfBBWidth = boundingBox.width / 2;
    const halfBBHeight = boundingBox.height / 2;
    const availX = halfBBWidth - Math.abs(localX);
    const availY = halfBBHeight - Math.abs(localY);

    // Find out how the inner rectangle is rotated relative to the bounding box.
    const innerAngleRad = fabric.util.degreesToRadians(innerRect.angle);
    const relativeAngle = innerAngleRad - bbAngleRad;
    const absCos = Math.abs(Math.cos(relativeAngle));
    const absSin = Math.abs(Math.sin(relativeAngle));

    // Get half the inner rectangle's dimensions.
    let width = innerRectWidth ? innerRectWidth : innerRect.width;
    let height = innerRectHeight ? innerRectHeight : innerRect.height;

    const halfW = width / 2;
    const halfH = height / 2;

    // Calculate the largest scale factor that keeps the inner rectangle fully inside the bounding box.
    // This factor is limited by both the horizontal and vertical available space.
    const sX = availX / (halfW * absCos + halfH * absSin);
    const sY = availY / (halfW * absSin + halfH * absCos);

    // Compute the maximum width and height after applying the scale factor.
    const maxWidth = width * sX;
    const maxHeight = height * sY;

    // Return the computed scale factor and the new dimensions.
    return {
        scaleX: sX,
        scaleY: sY,
        maxScale: Math.min(sX, sY),
        width: maxWidth,
        height: maxHeight
    };
};

/**
 * Calculates how much the inner rectangle can rotate (clockwise and counter-clockwise) while still staying fully inside of the bounding box.
 *
 * @param {object} boundingBox - The bounding box.
 * @param {object} innerRect - The inner rectangle.
 *
 * @returns {object} An object with:
 *   - `minRotationDelta`: Maximum allowed counter-clockwise rotation (in degrees, negative value).
 *   - `maxRotationDelta`: Maximum allowed clockwise rotation (in degrees, positive value).
 */
export const constrainRotationToBoundingBox = function (boundingBox, innerRect) {
    // Get the center and half-dimensions of the bounding box.
    const center = new fabric.Point(boundingBox.left, boundingBox.top);
    const halfWidth = boundingBox.width / 2;
    const halfHeight = boundingBox.height / 2;

    // Convert the innerRect's center into the bounding box coordinate system.
    const rectCenterInBB = fabric.util.rotatePoint(
        new fabric.Point(innerRect.left, innerRect.top),
        center,
        fabric.util.degreesToRadians(-boundingBox.angle)
    );

    // Compute available margins: the distance from the innerRect's center to the edges of the bounding box.
    const availX = halfWidth - Math.abs(rectCenterInBB.x - center.x);
    const availY = halfHeight - Math.abs(rectCenterInBB.y - center.y);

    // Get the base dimensions of the innerRect.
    const w = innerRect.width;
    const h = innerRect.height;

    // Calculate the current relative angle (in radians) of innerRect in the bounding box system.
    const currentRel = fabric.util.degreesToRadians(innerRect.angle - boundingBox.angle);

    // Function to check if innerRect fits inside the bounding box for a given relative angle.
    function isValid(theta) {
        const effectiveHalfWidth = (w * Math.abs(Math.cos(theta)) + h * Math.abs(Math.sin(theta))) / 2;
        const effectiveHalfHeight = (w * Math.abs(Math.sin(theta)) + h * Math.abs(Math.cos(theta))) / 2;
        return effectiveHalfWidth <= availX && effectiveHalfHeight <= availY;
    }

    // If the current angle is already outside the bounding box, no rotation is allowed.
    if (!isValid(currentRel)) {
        return { minRotationDelta: 0, maxRotationDelta: 0 };
    }

    // Find the maximum allowed delta (in radians) in one direction. To avoid scanning up to a
    // full rotation in 0.5° increments (~1440 isValid() checks on every rotate event when the
    // inner rect fits at every angle), first scan coarsely for the interval containing the
    // first invalid angle, then refine only that interval at the original 0.5° resolution.
    const coarseStep = fabric.util.degreesToRadians(5);
    const fineStep = fabric.util.degreesToRadians(0.5);
    const maxRange = Math.PI * 2;

    function findMaxDelta(direction) {
        let coarseTheta = currentRel; // known valid, checked above
        const maxCoarseSteps = Math.ceil(maxRange / coarseStep);

        for (let steps = 0; steps < maxCoarseSteps; steps++) {
            const nextCoarseTheta = coarseTheta + direction * coarseStep;
            if (!isValid(nextCoarseTheta)) {
                // The boundary lies within (coarseTheta, nextCoarseTheta]; refine it.
                let fineTheta = coarseTheta;
                while (
                    Math.abs(fineTheta - currentRel) < maxRange &&
                    isValid(fineTheta + direction * fineStep)
                ) {
                    fineTheta += direction * fineStep;
                }
                return Math.abs(fineTheta - currentRel);
            }
            coarseTheta = nextCoarseTheta;
        }
        // Valid for the entire range.
        return Math.abs(coarseTheta - currentRel);
    }

    const maxDeltaPositive = findMaxDelta(1);
    const maxDeltaNegative = findMaxDelta(-1);

    // Return the rotation deltas in degrees.
    return {
        minRotationDelta: -fabric.util.radiansToDegrees(maxDeltaNegative),
        maxRotationDelta: fabric.util.radiansToDegrees(maxDeltaPositive)
    };
};

/**
 * Checks whether attempting to rotate an inner rectangle by `attemptedDelta` degrees should be
 *  rejected outright because there's no room left in that direction. A 0 delta on the OTHER side
 *  just means "no more room that way" and doesn't block rotation towards the side that still has
 *  room. Intended for interactive drags, where each attempted delta is small and rejecting the
 *  whole step (instead of clamping it) is the expected "snap back" behavior.
 *
 * @param {object} boundingBox - The bounding box.
 * @param {object} innerRect - The inner rectangle.
 * @param {number} attemptedDelta - The requested rotation delta, in degrees.
 *
 * @returns {boolean} True if the rotation should be rejected entirely.
 */
export const isRotationDeltaBlocked = function (boundingBox, innerRect, attemptedDelta) {
    const { minRotationDelta, maxRotationDelta } = constrainRotationToBoundingBox(boundingBox, innerRect);
    const blockedPositive = attemptedDelta > 0 && maxRotationDelta === 0;
    const blockedNegative = attemptedDelta < 0 && minRotationDelta === 0;
    return blockedPositive || blockedNegative;
};

/**
 * Clamps a requested rotation delta down to however much room is actually available, instead of
 *  rejecting it outright. Use this for a single discrete rotation request (e.g. a rotate button)
 *  where the requested delta isn't guaranteed to be small - rejecting only when the room is
 *  exactly exhausted would let a large request overshoot past the boundary instead of stopping
 *  at it.
 *
 * @param {object} boundingBox - The bounding box.
 * @param {object} innerRect - The inner rectangle.
 * @param {number} delta - The requested rotation delta, in degrees.
 *
 * @returns {number} `delta` clamped to [minRotationDelta, maxRotationDelta], in degrees.
 */
export const clampRotationDelta = function (boundingBox, innerRect, delta) {
    if (!delta) return 0;
    const { minRotationDelta, maxRotationDelta } = constrainRotationToBoundingBox(boundingBox, innerRect);
    return Math.min(Math.max(delta, minRotationDelta), maxRotationDelta);
};